import { PriceLevelState, PriceLevelStateProvider, TicketTypeState } from "@/domain/ticket-selection";
import { HoldStateProvider } from "@/domain/ticket-selection/data/providers/HoldState.provider";
import { ArrayHelper } from "webshop-modules/helper/Array.helper";
import { StepperState } from "@/components/atoms/stepper/stepper-state.interface";

/**
 * PriceLevelStateCollectionProvider
 */
export class PriceLevelStateCollectionProvider {

    /**
     * whatItDoes
     * reduces all available priceLevels to highest ticket type amount value
     */
    public static getHighestTicketTypeAmount(priceLevelCollection: PriceLevelState[]): number {
        return priceLevelCollection.map(priceLevel => {
            return HoldStateProvider.getHighestTicketTypeAmount(
                PriceLevelStateProvider.getActiveHoldFor(priceLevel)
            );
        }).reduce(ArrayHelper.ToHighestValue, 0);
    }

    /**
     * whatItDoes
     * reduces all available priceLevels to highest hold maxTickets value
     */
    public static getHighestHoldMaximum(priceLevelCollection: PriceLevelState[]): number {
        return priceLevelCollection.map(priceLevel => {
            const activeHold = PriceLevelStateProvider.getActiveHoldFor(priceLevel);
            return !activeHold ? 0 : activeHold.maxTickets;
        }).reduce(ArrayHelper.ToHighestValue, 0);
    }

    /**
     * whatItDoes
     * reduces all available priceLevels to lowest ticket type max value
     */
    public static getLowestTicketTypeMaximum(priceLevelCollection: PriceLevelState[], maxTickets: number): number {
        return priceLevelCollection.map(priceLevel => {
            return HoldStateProvider.getLowestTicketTypeMaximum(
                maxTickets,
                PriceLevelStateProvider.getActiveHoldFor(priceLevel)
            );
        }).reduce(ArrayHelper.ToLowestValue, maxTickets);
    }

    /**
     * whatItDoes
     * returns updated PriceLevelState collection with single activated PriceLevel
     */
    public static getCollectionStateForActivated(
        priceLevel: PriceLevelState,
        collection: PriceLevelState[]
    ): PriceLevelState[] {
        return collection.map(state => {
            return state.priceLevelId === priceLevel.priceLevelId
            ? PriceLevelStateProvider.activate(state)
            : PriceLevelStateProvider.deactivate(state)
        });
    }

    /**
     * whatItDoes
     * returns updated PriceLevelState collection with single expanded PriceLevel
     */
    public static getCollectionStateForToggled(
        priceLevel: PriceLevelState,
        collection: PriceLevelState[]
    ): PriceLevelState[] {
        return collection.map(state => PriceLevelStateProvider.toggle(
                state,
                state.priceLevelId === priceLevel.priceLevelId && !state.open
            ));
    }

    /**
     * whatItDoes
     * returns updated PriceLevelState collection with updated TicketTypeState on active levels
     */
    public static provideUpdatedCollectionForActiveTicketTypes(
        updatedState: TicketTypeState[],
        collection: PriceLevelState[]
    ): PriceLevelState[] {
        return collection.map(
            priceLevel => PriceLevelStateProvider.updatedPriceLevelForActiveTicketTypes(updatedState, priceLevel)
        );
    }

    /**
     * whatItDoes
     * updates PromotionHold active state based on provided promotion id
     */
    public static updatedCollectionForPromotion(id: number, collection: PriceLevelState[]): PriceLevelState[] {
        return collection.map(priceLevel => {
            priceLevel.promotionHolds = priceLevel.promotionHolds.map(promotionHold => {
                promotionHold.active = id === promotionHold.id;

                return promotionHold;
            });

            return priceLevel;
        })
    }

    /**
     * whatItDoes
     * updates all TicketTypes to single amount and updates restrictions
     */
    public static provideCollectionForSingleSelectionStepperChange(
        state: StepperState,
        collection: PriceLevelState[]
    ): PriceLevelState[] {
        return collection.map(priceLevel => {
            priceLevel.promotionHolds = priceLevel.promotionHolds.map(promotion => {
                promotion.holds = promotion.holds.map(hold => {
                    hold.ticketTypes = hold.ticketTypes.map(ticketType => {
                        ticketType.amount = state.amount;
                        ticketType.canDecrease = ticketType.amount > ticketType.min && ticketType.amount !== 0;
                        ticketType.canIncrease = ticketType.amount <= (ticketType.max - ticketType.step);
                        return ticketType;
                    });
                    return hold;
                });
                return promotion;
            });
            return priceLevel;
        });
    }

    /**
     * whatItDoes
     * updates PriceLevelState collection submit state
     */
    public static provideUpdatedSubmitState(collection: PriceLevelState[]): PriceLevelState[] {
        return collection.map(priceLevel => PriceLevelStateProvider.updatedSubmitStateFor(priceLevel));
    }

}
